import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Container, Card, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { Package, RefreshCw, Check, Trash2 } from 'lucide-react';
import type { CpGpMatchedProduct } from '../../../../models/Coupang';
import { SkinSeoulProductType, type SkinSeoulAdminProduct } from '../../../../models/ProductTypes';
import {
  CMWorkflowStatus,
  CMRequestReason,
  WORKFLOW_STEP_LABELS,
  isWorkflowStepCompleted,
  normalizeWorkflowStatus,
} from '../../../../models/CatalogManagement';
import { cpGpMatchClient } from '../../../../infrastructure/CpGpMatchClient';
import { glowpickClient } from '../../../../infrastructure/GlowpickClient';
import {
  WorkflowOverviewSection,
  CMStatusHistory,
  MatchingInfoSection,
  ProductDetailsSection,
  WpUploadSection,
  ProductRestructureSection,
  ImageProcessingSection,
  StepHeader,
  CMRequestSection,
  CMPublishSection,
} from './components';
import type { RestructuredProduct } from '../../../../infrastructure/SourcingClient';
import { planStep1Completion } from './planStep1Completion';
import { combineMatchedProduct } from './combineMatchedProduct';
import { useCreateCMWorkflow, useCMWorkflowByGPI } from '../../../../apps/hooks/sourcing/useCMWorkflowDetail';
import { useCpGpMatch } from '../../../../apps/hooks/sourcing/useCpGpMatch';
import { useAuth } from '../../../../providers/AuthProvider';
import { getConfig } from '../../../../config';
import { useUploadMultipleToWordPress } from '../../../../apps/hooks/product/useProduct';

const MatchedProductDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cpProductId, cpItemId } = useParams<{ cpProductId: string; cpItemId: string }>();
  const { user } = useAuth();
  const [rescrapeLoading, setRescrapeLoading] = useState(false);
  const [rescrapeMessage, setRescrapeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Wave selection modal state
  const [showWaveModal, setShowWaveModal] = useState(false);
  const [selectedWave, setSelectedWave] = useState<string>('');
  const [pendingStepCompletion, setPendingStepCompletion] = useState<{
    status: CMWorkflowStatus;
    label: string;
  } | null>(null);

  // Memoize the product data from location state to prevent unnecessary re-renders
  const productDataFromState = useMemo(() => {
    return location.state?.productData as CpGpMatchedProduct | undefined;
  }, [location.state?.productData]);

  // Query 1: Fetch the CP-GP match data.
  // Route state seeds the first render via initialData only; the query always hits the
  // API so refetch (after Save Product Info) returns fresh data, not stale route state.
  const {
    data: cpGpMatch,
    isLoading: matchLoading,
    error: matchError,
    refetch: refetchCpGpMatch,
  } = useCpGpMatch(cpProductId, cpItemId, productDataFromState);

  // Query 2: Fetch Glowpick product data (runs in parallel once we have gpProductId)
  const {
    data: glowpickProductData,
    isLoading: glowpickLoading,
    error: glowpickError,
    refetch: refetchGlowpickProduct,
  } = useQuery({
    queryKey: ['glowpickProduct', cpGpMatch?.gpProductId],
    queryFn: async () => {
      const gpProductId = cpGpMatch?.gpProductId;
      if (!gpProductId) {
        throw new Error('No Glowpick product ID available');
      }

      console.info('Fetching Glowpick product data for:', gpProductId);
      const gpProduct = await glowpickClient.getProductById(gpProductId);
      console.info('✅ Glowpick product fetched successfully');
      return gpProduct;
    },
    enabled: Boolean(cpGpMatch?.gpProductId),
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Combine the data for backward compatibility
  const matchedProduct = useMemo(() => {
    return combineMatchedProduct(cpGpMatch, glowpickProductData);
  }, [cpGpMatch, glowpickProductData]);

  // Combined loading and error states
  const hasEmbeddedGlowpickProduct = Boolean(cpGpMatch?.gpDetailedProduct);
  const loading = matchLoading || (glowpickLoading && !hasEmbeddedGlowpickProduct && !glowpickError);
  const error = matchError || (!hasEmbeddedGlowpickProduct ? glowpickError : null);

  // Refetch function that refreshes both queries
  const refetchMatchedProduct = useCallback(async () => {
    const results = await Promise.all([
      refetchCpGpMatch(),
      ...(cpGpMatch?.gpProductId ? [refetchGlowpickProduct()] : []),
    ]);
    return results[0];
  }, [cpGpMatch?.gpProductId, refetchCpGpMatch, refetchGlowpickProduct]);

  // CM Workflow mutation and query
  const createCMWorkflowMutation = useCreateCMWorkflow();
  const { data: cmWorkflowData, refetch: refetchCMWorkflow } = useCMWorkflowByGPI(
    matchedProduct?.gpProductId || '',
    matchedProduct?.cpProductId,
    Boolean(matchedProduct?.gpProductId),
  );

  // Helper function to get history entry for a specific step
  const getStepHistory = useCallback(
    (status: CMWorkflowStatus) => {
      if (!cmWorkflowData || cmWorkflowData.length === 0) {
        return undefined;
      }
      const history = cmWorkflowData[0]?.cmStatusHistory;
      if (!history || history.length === 0) {
        return undefined;
      }
      // Find the most recent history entry where toStatus matches.
      // Normalize so legacy statuses (QA_VERIFIED/DELEO_UPLOADED) map to CM_REQUESTED.
      const historyEntries = history.filter((entry) => normalizeWorkflowStatus(entry.toStatus) === status);
      return historyEntries.length > 0 ? historyEntries[historyEntries.length - 1] : undefined;
    },
    [cmWorkflowData],
  );

  // Get SKUs from CM Workflow data
  const skusFromWorkflow = useMemo(() => {
    if (!cmWorkflowData || cmWorkflowData.length === 0) {
      return [];
    }
    const skuCpiiMappings = cmWorkflowData[0]?.skuCpiiMappings || [];
    return skuCpiiMappings.map((m: any) => m.sku).filter((sku: string) => Boolean(sku));
  }, [cmWorkflowData]);

  // Fetch products by SKU using React Query (prevents duplicate calls)
  const { data: fetchedProducts, isLoading: loadingProducts } = useQuery({
    queryKey: ['productsBySku', skusFromWorkflow.join(',')],
    queryFn: async () => {
      if (skusFromWorkflow.length === 0) {
        return [];
      }

      const config = getConfig();
      const skuParams = skusFromWorkflow.join(',');
      const response = await fetch(`${config.apiUrl}/products/admin?skus=${skuParams}`);
      const data = await response.json();

      if (Array.isArray(data)) {
        return data as SkinSeoulAdminProduct[];
      }
      return [];
    },
    enabled: skusFromWorkflow.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  const productsBySku = useMemo(() => fetchedProducts || [], [fetchedProducts]);

  // CM Publish state
  const [publishProducts, setPublishProducts] = useState<any[]>([]);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishingSkus, setPublishingSkus] = useState<Set<string>>(new Set());

  // Product details state for Review Product Details section
  const [productDetails, setProductDetails] = useState<SkinSeoulAdminProduct[]>([]);
  const [productDetailsLoading, setProductDetailsLoading] = useState(false);
  const lastFetchedProductDetailsSkusRef = useRef<string>('');
  const isFetchingProductDetailsRef = useRef(false);

  // Fetch product details for Review Product Details section
  const fetchProductDetails = useCallback(
    async (forceRefresh = false) => {
      if (!cmWorkflowData || cmWorkflowData.length === 0) {
        return;
      }

      const skuMappings = cmWorkflowData[0]?.skuCpiiMappings;
      if (!skuMappings || skuMappings.length === 0) {
        return;
      }

      // Filter for Variable and Simple products only
      const filteredSkus = skuMappings
        .filter(
          (mapping) =>
            mapping.type && (mapping.type.toLowerCase() === 'variable' || mapping.type.toLowerCase() === 'simple'),
        )
        .map((m) => m.sku)
        .filter((s) => s && s.trim() !== '');

      if (filteredSkus.length === 0) {
        return;
      }

      const skuParams = filteredSkus.sort().join(',');

      // Skip if we already fetched these exact SKUs (unless force refresh)
      if (!forceRefresh && lastFetchedProductDetailsSkusRef.current === skuParams) {
        console.info('⏭️ Skipping duplicate fetchProductDetails call');
        return;
      }

      // Prevent simultaneous calls
      if (isFetchingProductDetailsRef.current) {
        console.info('⏭️ Already fetching product details, skipping');
        return;
      }

      isFetchingProductDetailsRef.current = true;
      setProductDetailsLoading(true);
      try {
        console.info(`🔍 fetchProductDetails for SKUs: ${skuParams}${forceRefresh ? ' (FORCED)' : ''}`);
        const response = await fetch(`${getConfig().apiUrl}/products/admin?skus=${skuParams}`);
        const data = await response.json();
        setProductDetails(Array.isArray(data) ? data : []);
        lastFetchedProductDetailsSkusRef.current = skuParams;
      } catch (error) {
        console.error('Error fetching product details:', error);
        setProductDetails([]);
      } finally {
        setProductDetailsLoading(false);
        isFetchingProductDetailsRef.current = false;
      }
    },
    [cmWorkflowData],
  );

  // Fetch products for publishing using admin API
  const lastFetchedPublishProductsSkusRef = useRef<string>('');
  const isFetchingPublishProductsRef = useRef(false);

  const fetchPublishProducts = useCallback(
    async (forceRefresh = false) => {
      if (!cmWorkflowData || cmWorkflowData.length === 0) {
        return;
      }

      const skuMappings = cmWorkflowData[0]?.skuCpiiMappings;
      if (!skuMappings || skuMappings.length === 0) {
        return;
      }

      const skus = skuMappings.map((m) => m.sku).filter((s) => s && s.trim() !== '');
      if (skus.length === 0) {
        return;
      }

      const skuParams = skus.sort().join(',');

      // Skip if we already fetched these exact SKUs (unless force refresh)
      if (!forceRefresh && lastFetchedPublishProductsSkusRef.current === skuParams) {
        console.info('⏭️ Skipping duplicate fetchPublishProducts call');
        return;
      }

      // Prevent simultaneous calls
      if (isFetchingPublishProductsRef.current) {
        console.info('⏭️ Already fetching publish products, skipping');
        return;
      }

      isFetchingPublishProductsRef.current = true;
      setPublishLoading(true);
      try {
        console.info(`🔍 fetchPublishProducts for SKUs: ${skuParams}${forceRefresh ? ' (FORCED)' : ''}`);
        const response = await fetch(`${getConfig().apiUrl}/products/admin?skus=${skuParams}`);
        const data = await response.json();
        setPublishProducts(Array.isArray(data) ? data : []);
        lastFetchedPublishProductsSkusRef.current = skuParams;
      } catch (error) {
        console.error('Error fetching publish products:', error);
        setPublishProducts([]);
      } finally {
        setPublishLoading(false);
        isFetchingPublishProductsRef.current = false;
      }
    },
    [cmWorkflowData],
  );

  // Batch publish mutation
  const publishMutation = useUploadMultipleToWordPress();

  // Publish products using batch API (single or multiple)
  const handlePublishProducts = async (products: SkinSeoulAdminProduct[]) => {
    if (!matchedProduct?.gpProductId || !matchedProduct?.cpProductId) {
      alert('Missing required data (GPI or CPPI)');
      return;
    }

    const wave = cmWorkflowData?.[0]?.wave;
    if (!wave) {
      alert('Wave is not set in CM workflow!');
      return;
    }

    // Mark products as publishing
    products.forEach((p) => setPublishingSkus((prev) => new Set(prev).add(p.sku)));

    const batchRequest = {
      products: products.map((product) => ({
        sku: product.sku,
        type: product.type || 'simple',
        status: 'publish',
        stock_status: 'instock',
        meta_data: [],
      })),
      operator: user?.name || user?.email || 'unknown-user',
      gpi: matchedProduct.gpProductId,
      cppi: matchedProduct.cpProductId,
      wave: wave,
    };

    publishMutation.mutate(batchRequest, {
      onSuccess: async (response) => {
        const totalPublished = response?.totalCreated || products.length;
        alert(`Successfully published ${totalPublished} product${totalPublished !== 1 ? 's' : ''}!`);

        // Refresh the products list
        await fetchPublishProducts(true);
      },
      onError: (error) => {
        console.error('Publish error:', error);
        alert('Failed to publish products. Please check the console for details.');
      },
      onSettled: () => {
        // Clear publishing state
        products.forEach((p) => {
          setPublishingSkus((prev) => {
            const newSet = new Set(prev);
            newSet.delete(p.sku);
            return newSet;
          });
        });
      },
    });
  };

  // Publish a single product
  const handlePublishProduct = async (sku: string) => {
    const product = publishProducts.find((p) => p.sku === sku);
    if (!product) {
      alert('Product not found');
      return;
    }

    await handlePublishProducts([product]);
  };

  // Fetch product details when WP_UPLOADED is completed
  useEffect(() => {
    if (isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.WP_UPLOADED)) {
      fetchProductDetails();
    }
  }, [cmWorkflowData, fetchProductDetails]);

  // Fetch publish products when there are SKU mappings (regardless of step completion)
  useEffect(() => {
    const hasSKUMappings = cmWorkflowData?.[0]?.skuCpiiMappings?.some((m) => m.sku?.trim());
    if (hasSKUMappings) {
      fetchPublishProducts();
    }
  }, [cmWorkflowData, fetchPublishProducts]);

  // WP Sheet Editor was used if: only COMPLETED in history OR has SKUs but invalid wave
  const isWpSheetEditorUsed = useMemo(() => {
    const workflow = cmWorkflowData?.[0];
    if (!workflow) {
      return false;
    }

    const history = workflow.cmStatusHistory;
    const hasSkus = workflow.skuCpiiMappings?.some((m) => m.sku?.trim());
    const hasInvalidWave = !workflow.wave || workflow.wave === '0';

    // Only COMPLETED in history = bypassed normal workflow
    const onlyCompleted = history?.length === 1 && history[0]?.toStatus === 'COMPLETED';

    // Multiple history entries = went through workflow
    const wentThroughWorkflow = history && history.length > 1;

    console.info('🔍 WP Sheet Editor Detection:', {
      historyLength: history?.length,
      historyStatuses: history?.map((h) => h.toStatus),
      wave: workflow.wave,
      hasSkus,
      onlyCompleted,
      wentThroughWorkflow,
      decision: onlyCompleted || (hasSkus && hasInvalidWave && !wentThroughWorkflow),
    });

    // If went through workflow (multiple history entries), NOT WP Sheet Editor
    if (wentThroughWorkflow) {
      return false;
    }

    return onlyCompleted || (hasSkus && hasInvalidWave);
  }, [cmWorkflowData]);

  // Initialize selectedWave from existing data (CM workflow or matched product)
  useEffect(() => {
    const waveFromWorkflow = cmWorkflowData?.[0]?.wave;
    const waveFromProduct = matchedProduct?.wave;

    // If SKUs exist but no CM workflow (or no wave in CM workflow), mark as WP Sheet Editor Used
    if (isWpSheetEditorUsed) {
      if (!selectedWave) {
        setSelectedWave('0');
        console.info('📋 SKUs exist but no CM workflow found - WP Sheet Editor Used');
      }
      return;
    }

    // Use wave from CM workflow if it exists, otherwise use wave from matched product
    const initialWave = waveFromWorkflow || waveFromProduct || '';

    if (initialWave && !selectedWave) {
      setSelectedWave(initialWave);
      console.info('📋 Initialized wave from existing data:', initialWave);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmWorkflowData, matchedProduct?.wave, isWpSheetEditorUsed]);

  // Check if each step can be completed (using requirements from WORKFLOW_STEP_LABELS)
  const step1Status = useMemo(() => {
    const scAgentEngData = matchedProduct?.gpDetailedProduct?.scAgentEngData;
    const hasScAgentEngData = Boolean(scAgentEngData && Object.keys(scAgentEngData).length > 0);

    // Step 1 is enabled when scAgentEngData exists
    const isReady = hasScAgentEngData;

    let reason = '';
    if (!hasScAgentEngData) {
      reason = WORKFLOW_STEP_LABELS[0]?.requirement || '';
    }

    return {
      disabled: !isReady,
      reason,
    };
  }, [matchedProduct?.gpDetailedProduct?.scAgentEngData]);

  const step2Status = useMemo(() => {
    const isPrevStepCompleted = isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.PRODUCT_INFO_GENERATED);
    if (!isPrevStepCompleted) {
      return {
        disabled: true,
        reason: '⚠️ Previous step must be completed first',
      };
    }

    const hasRestructured = Boolean(
      matchedProduct?.ssRestructuredProducts && matchedProduct.ssRestructuredProducts.length > 0,
    );
    return {
      disabled: !hasRestructured,
      reason: !hasRestructured ? WORKFLOW_STEP_LABELS[1]?.requirement || '' : '',
    };
  }, [matchedProduct?.ssRestructuredProducts, cmWorkflowData]);

  const step3Status = useMemo(() => {
    const isPrevStepCompleted = isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CP_OPT_RESTRUCTURED);
    if (!isPrevStepCompleted) {
      return {
        disabled: true,
        reason: '⚠️ Previous step must be completed first',
      };
    }

    // Check if SKUs have been generated (products uploaded to WordPress)
    if (!cmWorkflowData || cmWorkflowData.length === 0) {
      return {
        disabled: true,
        reason: '⚠️ No CM Workflow found. Please upload products to WordPress to generate SKUs first.',
      };
    }
    const workflow = cmWorkflowData[0];
    if (!workflow?.skuCpiiMappings || workflow.skuCpiiMappings.length === 0) {
      return {
        disabled: true,
        reason: '⚠️ No SKU mappings found. Please upload products to WordPress to generate SKUs first.',
      };
    }
    const hasValidSku = workflow.skuCpiiMappings.some((mapping) => mapping.sku && mapping.sku.trim() !== '');
    if (!hasValidSku) {
      return {
        disabled: true,
        reason: '⚠️ No valid SKUs found. Please upload products to WordPress to generate SKUs first.',
      };
    }

    // All requirements met
    return {
      disabled: false,
      reason: '',
    };
  }, [cmWorkflowData]);

  const step4Status = useMemo(() => {
    const isPrevStepCompleted = isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.WP_UPLOADED);
    if (!isPrevStepCompleted) {
      return {
        disabled: true,
        reason: '⚠️ Previous step must be completed first',
      };
    }

    // Requirement 1: Check if SKUs have been generated
    if (!cmWorkflowData || cmWorkflowData.length === 0) {
      return {
        disabled: true,
        reason: '⚠️ No CM Workflow found. Please upload products to WordPress to generate SKUs first.',
      };
    }
    const workflow = cmWorkflowData[0];
    if (!workflow?.skuCpiiMappings || workflow.skuCpiiMappings.length === 0) {
      return {
        disabled: true,
        reason: '⚠️ No SKU mappings found. Please upload products to WordPress to generate SKUs first.',
      };
    }
    const hasValidSku = workflow.skuCpiiMappings.some((mapping) => mapping.sku && mapping.sku.trim() !== '');
    if (!hasValidSku) {
      return {
        disabled: true,
        reason: '⚠️ No valid SKUs found. Please upload products to WordPress to generate SKUs first.',
      };
    }

    // Requirement 2: Check if all SKUs have at least one productImageUrl
    if (productsBySku.length === 0) {
      return {
        disabled: true,
        reason: '⚠️ No products found. Please upload products to WordPress first.',
      };
    }

    const skusWithoutImages = productsBySku.filter(
      (product) =>
        product.type !== SkinSeoulProductType.Variation &&
        (!product.productImageUrls || product.productImageUrls.length === 0),
    );

    if (skusWithoutImages.length > 0) {
      const skuList = skusWithoutImages.map((p) => p.sku).join(', ');
      return {
        disabled: true,
        reason: `⚠️ The following SKUs have no images: ${skuList}. Each SKU must have at least one productImageUrl.`,
      };
    }

    console.info('🔍 Image Check:', {
      totalProducts: productsBySku.length,
      productsWithImages: productsBySku.filter((p) => p.productImageUrls && p.productImageUrls.length > 0).length,
      skusWithoutImages: skusWithoutImages.map((p) => p.sku),
    });

    // All requirements met
    return {
      disabled: false,
      reason: '',
    };
  }, [cmWorkflowData, productsBySku]);

  const step5Status = useMemo(() => {
    // Check if all previous steps are completed
    const isStep1Completed = isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.PRODUCT_INFO_GENERATED);
    const isStep2Completed = isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CP_OPT_RESTRUCTURED);
    const isStep3Completed = isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.WP_UPLOADED);
    const isStep4Completed = isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.IMG_PROCESSED);

    if (!isStep1Completed || !isStep2Completed || !isStep3Completed || !isStep4Completed) {
      return {
        disabled: true,
        reason: '⚠️ All previous steps (1-4) must be completed first',
      };
    }

    // All previous steps are completed, CM Request can be completed
    return {
      disabled: false,
      reason: '',
    };
  }, [cmWorkflowData]);

  const step6Status = useMemo(() => {
    const isPrevStepCompleted = isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CM_REQUESTED);
    if (!isPrevStepCompleted) {
      return {
        disabled: true,
        reason: '⚠️ Previous step must be completed first',
      };
    }

    // Allow completing step once previous step is done
    // Users can publish products individually, not all required
    return {
      disabled: false,
      reason: '',
    };
  }, [cmWorkflowData]);

  // Load saved SS Product Structure from matched product
  const savedSsStructure = useMemo(() => {
    if (!matchedProduct?.ssRestructuredProducts || matchedProduct.ssRestructuredProducts.length === 0) {
      console.info('No saved SS structure found in matched product');
      return null;
    }

    const products = matchedProduct.ssRestructuredProducts;
    console.info('✅ Loaded saved SS structure:', products.length, 'products');

    // Convert saved products array to the expected structure
    return {
      cpProductId: matchedProduct.cpProductId,
      products: products,
      stats: {
        cpOptionsCnt: products.length,
        variable: products.filter((p: RestructuredProduct) => p.type === 'variable').length,
        variation: products.filter((p: RestructuredProduct) => p.type === 'variation').length,
        simple: products.filter((p: RestructuredProduct) => p.type === 'simple').length,
        excluded: 0,
      },
    };
  }, [matchedProduct?.ssRestructuredProducts, matchedProduct?.cpProductId]);

  // Use saved data for SS structure
  const currentSsStructure = savedSsStructure;

  const handleRescrape = async () => {
    if (!matchedProduct) {
      return;
    }

    setRescrapeLoading(true);
    setRescrapeMessage(null);

    try {
      console.info('🔄 Starting re-scrape for matched product');

      const rescrapeData = {
        cpProductId: matchedProduct.cpProductId,
        cpItemId: matchedProduct.cpItemId,
        gpProductId: matchedProduct.gpProductId,
        keyword: matchedProduct.keyword,
        thumbnailImageUrl: matchedProduct.cpDetailedProduct.thumbnailImageUrl || '',
        productPageUrl: matchedProduct.cpDetailedProduct.productPageUrl || '',
      };
      console.info('🔄 rescrapeData', rescrapeData);

      const result = await cpGpMatchClient.createMatch(rescrapeData);

      if (result.success) {
        setRescrapeMessage({
          type: 'success',
          text: 'Re-scrape request submitted successfully. The product data will be updated shortly.',
        });
        console.info('✅ Re-scrape request submitted successfully');
      } else {
        setRescrapeMessage({
          type: 'error',
          text: result.message || 'Failed to submit re-scrape request. Please try again.',
        });
        console.error('❌ Re-scrape request failed:', result.message);
      }
    } catch (error) {
      setRescrapeMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.',
      });
      console.error('❌ Re-scrape error:', error);
    } finally {
      setRescrapeLoading(false);
    }
  };

  const handleUnmatch = async () => {
    if (!matchedProduct) {
      return;
    }

    const confirmed = confirm(
      `Are you sure you want to unmatch this product?\n\n` +
        `GP Product ID: ${matchedProduct.gpProductId}\n` +
        `CP Product ID: ${matchedProduct.cpProductId}\n` +
        `CP Item ID: ${matchedProduct.cpItemId}\n\n` +
        `This action will remove the match between Glowpick and Coupang products.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      console.info('🔄 Unmatching product...');

      const result = await cpGpMatchClient.deleteMatch(matchedProduct.cpProductId, matchedProduct.cpItemId);

      if (result.success) {
        setRescrapeMessage({
          type: 'success',
          text: 'Product unmatched successfully. Redirecting to matched products list...',
        });
        console.info('✅ Product unmatched successfully');

        // Redirect to matched products list after a short delay
        setTimeout(() => {
          navigate('/sourcing/matched');
        }, 1500);
      } else {
        setRescrapeMessage({
          type: 'error',
          text: result.message || 'Failed to unmatch product. Please try again.',
        });
        console.error('❌ Unmatch failed:', result.message);
      }
    } catch (error) {
      setRescrapeMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.',
      });
      console.error('❌ Unmatch error:', error);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-3">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading product details...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-3">
        <div className="text-center py-5">
          <Package size={48} className="text-muted mb-3" />
          <h5>Error Loading Product</h5>
          <p className="text-muted">{error instanceof Error ? error.message : 'An unexpected error occurred'}</p>
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="primary" onClick={() => navigate('/sourcing/matched')}>
              Back to Matched Products
            </Button>
            <Button variant="outline-secondary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  if (!matchedProduct) {
    return (
      <Container fluid className="py-3">
        <div className="text-center py-5">
          <Package size={48} className="text-muted mb-3" />
          <h5>Product not found</h5>
          <p className="text-muted">The requested product could not be found.</p>
          <Button variant="primary" onClick={() => navigate('/sourcing/matched')}>
            Back to Matched Products
          </Button>
        </div>
      </Container>
    );
  }

  const coupangProduct = matchedProduct.cpDetailedProduct;
  const glowpickProduct = matchedProduct.gpDetailedProduct;

  // Safety check for required data
  if (!coupangProduct) {
    return (
      <Container fluid className="py-3">
        <div className="text-center py-5">
          <Package size={48} className="text-muted mb-3" />
          <h5>Incomplete Product Data</h5>
          <p className="text-muted">Product details are missing. Please try re-scraping the product.</p>
          <Button variant="primary" onClick={() => navigate('/sourcing/matched')}>
            Back to Matched Products
          </Button>
        </div>
      </Container>
    );
  }

  // Create unified product data
  const unifiedProduct = {
    // Primary info - prefer Glowpick for product details, Coupang for commerce
    name: glowpickProduct?.name || coupangProduct?.name,
    brand: glowpickProduct?.brand || coupangProduct?.brand,
    category: glowpickProduct?.category,
    subcategory: glowpickProduct?.subcategory,
    size: glowpickProduct?.size,

    // Images - combine both sources (prefer S3 over CDN)
    thumbnailImage: coupangProduct?.thumbnailImageUrl || [],
    heroImages: coupangProduct?.heroImageUrls || [],
    detailedImages: coupangProduct?.detailedImageUrls || [],

    // Pricing - Coupang pricing (support both old and new structure)
    priceInfo: coupangProduct?.priceInfo || {
      salePrice:
        ((coupangProduct as any).salePrice ? (coupangProduct as any).salePrice.toLocaleString() + '원' : undefined) ||
        ((coupangProduct as any).unitPrice ? (coupangProduct as any).unitPrice.toLocaleString() + '원' : undefined),
      originalPrice: (coupangProduct as any).originalPrice
        ? (coupangProduct as any).originalPrice.toLocaleString() + '원'
        : undefined,
      discountRate: (coupangProduct as any).discountRate,
      unitPrice: (coupangProduct as any).unitPrice
        ? `(단가: ${(coupangProduct as any).unitPrice.toLocaleString()}원)`
        : undefined,
    },

    // Seller information from Coupang
    sellerInfo: coupangProduct?.sellerInfo,

    // Ratings - combine both
    glowpickRating: glowpickProduct?.rating,
    glowpickReviews: glowpickProduct?.reviews,
    coupangRating: coupangProduct?.rating,
    coupangReviews: coupangProduct?.reviews,

    // Quality indicators from Glowpick
    awards: glowpickProduct?.awards || [],
    rankings: glowpickProduct?.rankings || {},

    // Product details
    description: glowpickProduct?.description,
    tags: glowpickProduct?.tags || [],
    ingredients: glowpickProduct?.ingredients || [],

    // Commerce details from Coupang
    shipping: coupangProduct?.shipping,
    shippingInfo: coupangProduct?.shippingInfo,

    options: coupangProduct?.options,

    // URLs
    glowpickUrl: glowpickProduct?.url,
    coupangUrl: coupangProduct?.productPageUrl,
  };

  // Complete step directly without showing modal (when wave already exists)
  const handleCompleteStepWithWaveDirectly = async (nextStep: CMWorkflowStatus, stepLabel: string, wave: string) => {
    if (!matchedProduct) {
      return;
    }

    try {
      const savedProducts = matchedProduct.ssRestructuredProducts || [];

      // Build skuCpiiMappings from restructured products (only include if both sku and cpii exist)
      const skuCpiiMappings = savedProducts
        .filter((product: any) => product.cpItemId && product.sku)
        .map((product: any) => ({
          sku: product.sku,
          cpii: product.cpItemId,
          type: product.type,
        }));

      const cmWorkflowPayload = {
        gpi: matchedProduct.gpProductId,
        cppi: matchedProduct.cpProductId,
        ...(skuCpiiMappings.length > 0 && { skuCpiiMappings }),
        cmStatus: nextStep,
        wave,
        cmOperation: {
          operator: user?.name || user?.email || 'unknown-user',
          reason: CMRequestReason.NEW_MATCH,
          message: `${stepLabel} completed`,
        },
      };

      await createCMWorkflowMutation.mutateAsync(cmWorkflowPayload);

      console.info(`✅ API CALL COMPLETED: CM Workflow created with status ${nextStep}`);

      const workflowResult = await refetchCMWorkflow();
      if (workflowResult.data) {
        console.info('📋 CM Workflow data fetched:', workflowResult.data);
        console.info('📊 Completed steps:', workflowResult.data.map((item) => item.cmStatus).join(', '));
      }

      // Refetch matched product to reflect changes
      await refetchMatchedProduct();
    } catch (error) {
      console.error('❌ Error updating workflow status:', error);
      alert(`Error updating status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Show wave selection modal (directly complete if wave is selected)
  const handleShowWaveModal = (nextStep: CMWorkflowStatus, stepLabel: string) => {
    // Check if wave is already selected (from local state)
    if (selectedWave) {
      // Wave already selected, directly complete the step without showing modal
      setPendingStepCompletion({ status: nextStep, label: stepLabel });
      // Directly trigger the completion
      handleCompleteStepWithWaveDirectly(nextStep, stepLabel, selectedWave);
    } else {
      // No wave selected yet, require wave selection first
      alert('⚠️ Please select a Wave first before completing this step!');
    }
  };

  // Complete workflow step with wave
  const handleCompleteStepWithWave = async () => {
    if (!pendingStepCompletion || !matchedProduct) {
      return;
    }

    if (!selectedWave) {
      alert('Please select a wave before completing the step.');
      return;
    }

    const { status: nextStep, label: stepLabel } = pendingStepCompletion;

    try {
      const wave = selectedWave;

      const savedProducts = matchedProduct.ssRestructuredProducts || [];

      // Build skuCpiiMappings from restructured products (only include if both sku and cpii exist)
      const skuCpiiMappings = savedProducts
        .filter((product: any) => product.cpItemId && product.sku)
        .map((product: any) => ({
          sku: product.sku,
          cpii: product.cpItemId,
          type: product.type,
        }));

      const cmWorkflowPayload = {
        gpi: matchedProduct.gpProductId,
        cppi: matchedProduct.cpProductId,
        ...(skuCpiiMappings.length > 0 && { skuCpiiMappings }),
        cmStatus: nextStep,
        wave,
        cmOperation: {
          operator: user?.name || user?.email || 'unknown-user',
          reason: CMRequestReason.NEW_MATCH,
          message: `${stepLabel} completed`,
        },
      };

      await createCMWorkflowMutation.mutateAsync(cmWorkflowPayload);

      console.info(`✅ API CALL #1 COMPLETED: CM Workflow created with status ${nextStep}`);

      const workflowResult = await refetchCMWorkflow();
      if (workflowResult.data) {
        console.info('📋 CM Workflow data fetched:', workflowResult.data);
        console.info('📊 Completed steps:', workflowResult.data.map((item) => item.cmStatus).join(', '));
      }

      // Refetch matched product to reflect changes
      await refetchMatchedProduct();

      // Close modal and reset
      setShowWaveModal(false);
      setPendingStepCompletion(null);
      setSelectedWave('');
    } catch (error) {
      console.error('❌ Error updating workflow status:', error);
      alert(`Error updating status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Cancel wave selection
  const handleCancelWaveSelection = () => {
    setShowWaveModal(false);
    setPendingStepCompletion(null);
    setSelectedWave('');
  };

  // Handle wave change from WorkflowStatus dropdown
  const handleWaveChange = async (newWave: string) => {
    if (!matchedProduct) {
      return;
    }

    // Just store the wave selection locally - don't call any API
    // The wave will be saved when a step is actually completed
    setSelectedWave(newWave);
    console.info(`✅ Wave selected: ${newWave} (will be saved when step is completed)`);
  };

  // Handle GP-CP Matched completion
  const handleCompleteGpCpMatched = async () => {
    if (!matchedProduct) {
      return;
    }

    // POST /cmWorkflow upserts by gpi+cppi, so completion must not be gated on whether a
    // workflow record already exists — legacy COMPLETED matches have one and were silently
    // no-op'd before. planStep1Completion validates wave/scAgentEngData and builds the payload.
    const plan = planStep1Completion(matchedProduct, selectedWave, user?.name || user?.email || 'unknown-user');
    if (plan.kind === 'blocked') {
      alert(plan.message);
      return;
    }

    try {
      console.info('Upserting CM Workflow with wave:', plan.payload);

      await createCMWorkflowMutation.mutateAsync(plan.payload);
      await refetchCMWorkflow();
      await refetchMatchedProduct();

      console.info('GP-CP Matched completed successfully with wave:', plan.payload.wave);
    } catch (error) {
      console.error('Error completing GP-CP Matched:', error);
      alert(`Error completing step: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <Container fluid className="py-2">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">GP-CP Matched To Catalog</h4>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={() => navigate('/sourcing/matched')}>
            Go to GP-CP Matched List
          </Button>
          {matchedProduct?.gpProductId && (
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => navigate(`/sourcing/matching/glowpickId/${matchedProduct.gpProductId}`)}
            >
              Go to GP-CP Matching ({matchedProduct.gpProductId})
            </Button>
          )}
          <Button
            variant="warning"
            size="sm"
            onClick={handleRescrape}
            disabled={rescrapeLoading}
            className="d-flex align-items-center gap-1"
          >
            {rescrapeLoading ? <Spinner animation="border" size="sm" /> : <RefreshCw size={14} />}
            {rescrapeLoading ? 'Re-scraping...' : 'Re-scrape'}
          </Button>
          <Button variant="danger" size="sm" onClick={handleUnmatch} className="d-flex align-items-center gap-1">
            <Trash2 size={14} />
            Unmatch
          </Button>
        </div>
      </div>

      {/* Re-scrape Message */}
      {rescrapeMessage && (
        <Alert
          variant={rescrapeMessage.type === 'success' ? 'success' : 'danger'}
          className="mb-4"
          dismissible
          onClose={() => setRescrapeMessage(null)}
        >
          {rescrapeMessage.text}
        </Alert>
      )}

      {/* CM Workflow Overview */}
      <Card className="mb-4">
        <Card.Header className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <h6 className="mb-0">CM Workflow Overview</h6>
          </div>
          <CMStatusHistory
            {...(cmWorkflowData?.[0]?.cmStatus ? { currentStatus: cmWorkflowData[0].cmStatus } : {})}
            {...(cmWorkflowData?.[0]?.cmStatusHistory ? { statusHistory: cmWorkflowData[0].cmStatusHistory } : {})}
            {...(matchedProduct.gpProductId ? { gpi: matchedProduct.gpProductId } : {})}
          />
        </Card.Header>
        <Card.Body>
          <WorkflowOverviewSection
            currentStatus={cmWorkflowData?.[0]?.cmStatus}
            wave={selectedWave}
            onWaveChange={handleWaveChange}
            isWpSheetEditorUsed={isWpSheetEditorUsed || false}
          />
        </Card.Body>
      </Card>

      {/* Matching Information */}
      <MatchingInfoSection
        matchedProduct={matchedProduct}
        glowpickProduct={glowpickProduct}
        cmWorkflowData={cmWorkflowData}
        cpProductId={cpProductId}
        cpItemId={cpItemId}
        refetchMatchedProduct={refetchCpGpMatch}
      />

      {/* Step 1 Header */}
      <StepHeader
        stepNumber={1}
        title={
          isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.PRODUCT_INFO_GENERATED)
            ? WORKFLOW_STEP_LABELS[0]?.labelAfter || ''
            : WORKFLOW_STEP_LABELS[0]?.labelBefore || ''
        }
        description={WORKFLOW_STEP_LABELS[0]?.description || ''}
        isCompleted={isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.PRODUCT_INFO_GENERATED)}
        sectionId="step-1-section"
        onComplete={handleCompleteGpCpMatched}
        disabled={step1Status.disabled}
        disabledReason={step1Status.reason}
      />

      {/* Product Details Section - Product Comparison */}
      <ProductDetailsSection
        matchedProduct={matchedProduct}
        glowpickProduct={glowpickProduct}
        coupangProduct={coupangProduct}
        unifiedProduct={unifiedProduct}
        cpProductId={cpProductId}
        cpItemId={cpItemId}
        onRefetch={refetchMatchedProduct}
      />

      {/* Step 2 Header */}
      <StepHeader
        stepNumber={2}
        title={
          isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CP_OPT_RESTRUCTURED)
            ? WORKFLOW_STEP_LABELS[1]?.labelAfter || ''
            : WORKFLOW_STEP_LABELS[1]?.labelBefore || ''
        }
        description={WORKFLOW_STEP_LABELS[1]?.description || ''}
        isCompleted={isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CP_OPT_RESTRUCTURED)}
        sectionId="step-2-section"
        marginTop={5}
        onComplete={() =>
          handleShowWaveModal(CMWorkflowStatus.CP_OPT_RESTRUCTURED, WORKFLOW_STEP_LABELS[1]?.labelAfter || 'Step 2')
        }
        disabled={step2Status.disabled}
        disabledReason={step2Status.reason}
      />

      {/* Product Restructure Section */}
      <ProductRestructureSection
        matchedProduct={matchedProduct}
        unifiedProduct={unifiedProduct}
        onRefetch={refetchMatchedProduct}
      />

      {/* Step 3 Header - Upload Product to WordPress */}
      <StepHeader
        stepNumber={3}
        title={
          isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.WP_UPLOADED)
            ? WORKFLOW_STEP_LABELS[2]?.labelAfter || ''
            : WORKFLOW_STEP_LABELS[2]?.labelBefore || ''
        }
        description={WORKFLOW_STEP_LABELS[2]?.description || ''}
        isCompleted={isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.WP_UPLOADED)}
        sectionId="step-3-section"
        marginTop={5}
        onComplete={() =>
          handleShowWaveModal(CMWorkflowStatus.WP_UPLOADED, WORKFLOW_STEP_LABELS[2]?.labelAfter || 'Step 3')
        }
        disabled={step3Status.disabled}
        disabledReason={step3Status.reason}
      />

      {/* WP Upload Section */}
      <WpUploadSection
        currentSsStructure={currentSsStructure}
        matchedProduct={matchedProduct}
        cmWorkflowData={cmWorkflowData}
        matchLoading={matchLoading}
        refetchMatchedProduct={refetchMatchedProduct}
        refetchCMWorkflow={refetchCMWorkflow}
        refetchProductDetails={fetchProductDetails}
      />

      {/* Step 4 Header - Media Sync */}
      <StepHeader
        stepNumber={4}
        title={
          isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.IMG_PROCESSED)
            ? WORKFLOW_STEP_LABELS[3]?.labelAfter || ''
            : WORKFLOW_STEP_LABELS[3]?.labelBefore || ''
        }
        description={WORKFLOW_STEP_LABELS[3]?.description || ''}
        isCompleted={isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.IMG_PROCESSED)}
        sectionId="step-4-section"
        marginTop={5}
        onComplete={() =>
          handleShowWaveModal(CMWorkflowStatus.IMG_PROCESSED, WORKFLOW_STEP_LABELS[3]?.labelAfter || 'Step 4')
        }
        disabled={step4Status.disabled}
        disabledReason={step4Status.reason}
      />
      {/* Image Processing with Editor Access */}
      <ImageProcessingSection
        matchedProduct={matchedProduct}
        productsBySku={productsBySku}
        loadingProducts={loadingProducts}
        onRefresh={refetchMatchedProduct}
      />

      {/* Step 5 Header */}
      <StepHeader
        stepNumber={5}
        title={
          isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CM_REQUESTED)
            ? WORKFLOW_STEP_LABELS[4]?.labelAfter || ''
            : WORKFLOW_STEP_LABELS[4]?.labelBefore || ''
        }
        description={WORKFLOW_STEP_LABELS[4]?.description || ''}
        isCompleted={isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CM_REQUESTED)}
        sectionId="step-5-section"
        marginTop={5}
        onComplete={() =>
          handleShowWaveModal(CMWorkflowStatus.CM_REQUESTED, WORKFLOW_STEP_LABELS[4]?.labelAfter || 'Step 5')
        }
        disabled={step5Status.disabled}
        disabledReason={step5Status.reason}
      />

      {/* CM Request Section */}
      <CMRequestSection
        cmWorkflowData={cmWorkflowData}
        getStepHistory={getStepHistory}
        productDetails={productDetails}
        productDetailsLoading={productDetailsLoading}
        step5Status={step5Status}
        handleShowWaveModal={handleShowWaveModal}
        fetchProductDetails={fetchProductDetails}
      />

      {/* Step 6 Header - CM Publish */}
      <StepHeader
        stepNumber={6}
        title={
          isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CM_PUBLISHED)
            ? WORKFLOW_STEP_LABELS[5]?.labelAfter || ''
            : WORKFLOW_STEP_LABELS[5]?.labelBefore || ''
        }
        description={WORKFLOW_STEP_LABELS[5]?.description || ''}
        isCompleted={isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CM_PUBLISHED)}
        sectionId="step-6-section"
        marginTop={5}
        onComplete={() =>
          handleShowWaveModal(CMWorkflowStatus.CM_PUBLISHED, WORKFLOW_STEP_LABELS[5]?.labelAfter || 'Step 6')
        }
        disabled={step6Status.disabled}
        disabledReason={step6Status.reason}
      />

      {/* CM Publish Section */}
      <CMPublishSection
        publishProducts={publishProducts}
        publishLoading={publishLoading}
        publishingSkus={publishingSkus}
        publishMutation={publishMutation}
        handlePublishProduct={handlePublishProduct}
        handlePublishProducts={handlePublishProducts}
        fetchPublishProducts={fetchPublishProducts}
        cmWorkflowData={cmWorkflowData}
        isWorkflowStepCompleted={isWorkflowStepCompleted}
        CMWorkflowStatus={CMWorkflowStatus}
      />

      {/* Wave Selection Modal */}
      <Modal show={showWaveModal} onHide={handleCancelWaveSelection} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Wave</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Wave <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={selectedWave}
                onChange={(e) => setSelectedWave(e.target.value)}
                isInvalid={!selectedWave}
              >
                <option value="">Select wave...</option>
                <option value="6">Wave 6</option>
              </Form.Select>
              {!selectedWave && <Form.Control.Feedback type="invalid">Please select a wave</Form.Control.Feedback>}
              <Form.Text className="text-muted">Select the wave for this catalog management workflow step.</Form.Text>
            </Form.Group>

            {pendingStepCompletion && (
              <Alert variant="info" className="mb-0">
                <small>
                  <strong>Step:</strong> {pendingStepCompletion.label}
                  <br />
                  <strong>Status:</strong> {pendingStepCompletion.status}
                </small>
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelWaveSelection}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleCompleteStepWithWave} disabled={!selectedWave}>
            <Check size={16} className="me-1" />
            Complete Step
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom CSS for animations */}
      <style>
        {`
          @keyframes loading-bar {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}
      </style>
    </Container>
  );
};

export default MatchedProductDetailPage;
